import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LoadingSpinner } from './LoadingSpinner';
import { 
  getSleeperUser,
  listUserLeagues,
  importRosterAndSettingsFromSleeper,
  type SleeperUser,
  type SleeperLeague,
  type SleeperImportResult
} from '@/utils/sleeperRosterIntegration';
import type { Player } from '@/types/player';
import type { LeagueSettings } from '@/types/trade';

interface SleeperImportProps {
  onImportSuccess: (players: Player[], leagueSettings?: LeagueSettings, leagueName?: string, starterPlayerIds?: string[]) => void;
  availablePlayers: Player[];
}

const triggerClass =
  'inline-flex min-h-10 items-center justify-center gap-2 rounded-full bg-fiesta-yellow px-4 py-2 text-sm font-semibold text-ink transition-transform hover:scale-[1.02]';

export const SleeperImport: React.FC<SleeperImportProps> = ({ onImportSuccess, availablePlayers }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<'username' | 'leagues' | 'importing'>('username');
  const [username, setUsername] = useState('');
  const [sleeperUser, setSleeperUser] = useState<SleeperUser | null>(null);
  const [leagues, setLeagues] = useState<SleeperLeague[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFindUser = async () => {
    if (!username.trim()) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const user = await getSleeperUser(username.trim());
      if (user) {
        setSleeperUser(user);
        
        const { leagues: userLeagues, season } = await listUserLeagues(user.user_id);
        if (userLeagues.length === 0) {
          setError(`No leagues found for ${user.display_name} in the current or previous Sleeper season.`);
        } else {
          setLeagues(userLeagues);
          setStep('leagues');
          if (season !== String(new Date().getFullYear())) {
            setError(`Showing ${season} leagues.`);
          }
        }
      } else {
        setError('Sleeper user not found. Please check the username.');
      }
    } catch (err) {
      setError('Failed to find user. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImportRoster = async (leagueId: string) => {
    if (!sleeperUser) return;
    
    setStep('importing');
    setIsLoading(true);
    setError(null);
    
    try {
      const result: SleeperImportResult = await importRosterAndSettingsFromSleeper(
        sleeperUser.user_id,
        leagueId,
        availablePlayers
      );
      
      if (result.success) {
        onImportSuccess(result.players, result.leagueSettings, result.leagueName, result.starterPlayerIds);
        setIsOpen(false);
        resetState();
      } else {
        setError(result.error || 'Failed to import roster');
        setStep('leagues');
      }
    } catch (err) {
      setError('Failed to import roster. Please try again.');
      setStep('leagues');
    } finally {
      setIsLoading(false);
    }
  };

  const resetState = () => {
    setStep('username');
    setUsername('');
    setSleeperUser(null);
    setLeagues([]);
    setError(null);
  };

  const handleClose = () => {
    setIsOpen(false);
    resetState();
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={triggerClass}
      >
        <img src="/images/sleeper.png" alt="" className="h-4 w-4 object-contain" />
        Import from Sleeper
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={handleClose}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="max-h-[80vh] w-full max-w-md overflow-y-auto rounded-2xl border border-white/10 bg-ink p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mb-6 flex items-center justify-between">
                <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-white">
                  <img src="/images/sleeper.png" alt="" className="h-5 w-5 object-contain" />
                  Import from Sleeper
                </h3>
                <button
                  type="button"
                  onClick={handleClose}
                  className="text-zinc-500 hover:text-white"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {step === 'username' && (
                  <div className="space-y-4">
                    <p className="text-sm text-zinc-400">
                      Enter your Sleeper username to pull this league and its scoring.
                    </p>
                    
                    <div>
                      <label className="mb-2 block text-sm text-zinc-400">
                        Sleeper username
                      </label>
                      <input
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleFindUser()}
                        className="w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-white placeholder:text-zinc-500"
                        placeholder="Your Sleeper username"
                        autoFocus
                      />
                    </div>
                    
                    {error && (
                      <div className="text-sm text-fiesta-pink">{error}</div>
                    )}
                    
                    <button
                      type="button"
                      onClick={handleFindUser}
                      disabled={!username.trim() || isLoading}
                      className="flex w-full items-center justify-center gap-2 rounded-full bg-fiesta-yellow px-4 py-2 text-sm font-medium text-ink disabled:opacity-50"
                    >
                      {isLoading ? (
                        <>
                          <LoadingSpinner size="sm" />
                          Finding User...
                        </>
                      ) : (
                        'Find Leagues'
                      )}
                    </button>
                  </div>
                )}

                {step === 'leagues' && sleeperUser && (
                  <div className="space-y-4">
                      <div className="text-center">
                      <div className="mb-1 text-sm text-zinc-400">Found user</div>
                        <div className="font-medium text-white">{sleeperUser.display_name}</div>
                    </div>
                    
                    <div>
                      <div className="mb-3 text-sm text-zinc-400">
                        Pick a league to import
                      </div>
                      <div className="max-h-60 space-y-2 overflow-y-auto">
                        {leagues.map((league) => (
                          <button
                            type="button"
                            key={league.league_id}
                            className="w-full rounded-xl border border-white/10 p-3 text-left hover:bg-white/5"
                            onClick={() => handleImportRoster(league.league_id)}
                          >
                            <div className="font-medium text-white">{league.name}</div>
                            <div className="mt-1 text-xs text-zinc-500">
                              {league.total_rosters} teams · {league.status} · {league.season}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {error && (
                      <div className="text-sm text-fiesta-pink">{error}</div>
                    )}
                    
                    <button
                      type="button"
                      onClick={() => setStep('username')}
                      className="w-full rounded-full border border-white/10 px-4 py-2 text-sm text-zinc-300"
                    >
                      Back
                    </button>
                  </div>
                )}

                {step === 'importing' && (
                  <div className="space-y-4 text-center">
                    <LoadingSpinner size="lg" />
                    <div className="text-zinc-300">
                      Importing your roster and league settings from Sleeper
                    </div>
                    <div className="text-sm text-zinc-500">
                      Matching players and scoring to this board.
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}; 